import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '@stores/RootStore';
import sessionStorage from '@repositories/browserStorage/sessionStorage/SessionStorage';
import type { OAuthProvider } from '@repositories/oauthRepository/oauth.types';
import { useOAuthSignIn } from '@queryHooks/useOauth';
import { cleanUrlParams, getUrlParam, getUrlError } from '@utils/urlUtils';

/** provider별 에러 메시지 i18n 키 */
const ERROR_MESSAGE_KEYS: Record<string, string> = {
  microsoft: 'Sign_In_07',
  google: 'Sign_In_02',
};

/**
 * OAuth 콜백 처리를 위한 커스텀 훅 (Google/Microsoft 등 Splitter 기본 제공 SSO)
 *
 * 기능:
 * - Microsoft/Google OAuth 콜백 처리
 * - URL 파라미터 정리
 * - 에러 처리 및 사용자 알림
 * - 성공 시 후처리 로직 실행
 */
export const useOAuthCallback = () => {
  const { t } = useTranslation();
  const { mutate: oauthSignIn } = useOAuthSignIn();
  const {
    uiStore: { alertStore },
  } = useStore();

  /**
   * 통합 OAuth 콜백 처리 함수
   * @param provider - OAuth 제공자 ('microsoft' | 'google')
   * @param loginPostProcess - 로그인 성공 후 실행할 함수
   * @param onComplete - 성공/실패와 관계없이 처리 완료 후 실행할 함수
   */
  const handleOAuthCallback = useCallback(
    (provider: OAuthProvider, loginPostProcess: () => void, onComplete?: () => void) => {
      const code = getUrlParam('code');
      const error = getUrlError();
      const errorMessageKey = ERROR_MESSAGE_KEYS[provider] ?? 'Sign_In_02';

      if (error) {
        cleanUrlParams();
        alertStore.open({
          title: t('Sign_In_01'),
          message: t(errorMessageKey),
          useCancel: false,
        });
        onComplete?.();
        return;
      }

      if (code) {
        oauthSignIn(
          {
            provider,
            data: { authCode: code },
          },
          {
            onSuccess: () => {
              cleanUrlParams();
              loginPostProcess();
              onComplete?.();
            },
            onError: () => {
              cleanUrlParams();
              alertStore.open({
                title: t('Sign_In_01'),
                message: t(errorMessageKey),
                useCancel: false,
              });
              onComplete?.();
            },
          },
        );
      } else {
        onComplete?.();
      }
    },
    [oauthSignIn, alertStore, t],
  );

  return {
    handleOAuthCallback,
  };
};

/**
 * 동적 SSO 프로바이더 콜백 처리를 위한 커스텀 훅 (테넌트별 등록된 private SSO)
 *
 * 기능:
 * - OIDC 리다이렉트 후 콜백 처리
 * - URL 파라미터에서 authorization code 추출
 * - sessionStorage에서 registrationId/tenantId 복원
 * - 백엔드 인증 요청 및 에러 처리
 */
export const useSsoCallback = () => {
  const { t } = useTranslation();
  const { mutate: oauthSignIn } = useOAuthSignIn();
  const {
    uiStore: { alertStore },
  } = useStore();

  /**
   * 동적 SSO 프로바이더 콜백 처리
   * lookup으로 조회한 SSO 프로바이더의 registrationId를 사용하여 백엔드에 인증 요청
   * @param loginPostProcess - 로그인 성공 후 실행할 함수
   * @param onComplete - 성공/실패와 관계없이 처리 완료 후 실행할 함수
   */
  const handleSsoCallback = useCallback(
    (loginPostProcess: () => void, onComplete?: () => void) => {
      const code = getUrlParam('code');
      const error = getUrlError();
      const rawRegistrationId = sessionStorage.get('sso_registration_id');
      const registrationId = typeof rawRegistrationId === 'string' ? rawRegistrationId : undefined;
      const rawSsoTenantId = sessionStorage.get('sso_tenant_id');
      const ssoTenantId = typeof rawSsoTenantId === 'string' ? rawSsoTenantId : undefined;

      if (error) {
        cleanUrlParams();
        alertStore.open({
          title: t('Sign_In_01'),
          message: t('SsoProvider.Login.CallbackError'),
          useCancel: false,
        });
        onComplete?.();
        return;
      }

      if (code && registrationId) {
        oauthSignIn(
          {
            provider: registrationId,
            data: { authCode: code },
            overrideTenantId: ssoTenantId,
          },
          {
            onSuccess: () => {
              cleanUrlParams();
              loginPostProcess();
              onComplete?.();
            },
            onError: () => {
              cleanUrlParams();
              alertStore.open({
                title: t('Sign_In_01'),
                message: t('SsoProvider.Login.CallbackError'),
                useCancel: false,
              });
              onComplete?.();
            },
          },
        );
      } else {
        onComplete?.();
      }
    },
    [oauthSignIn, alertStore, t],
  );

  return {
    handleSsoCallback,
  };
};
